"use client";
import Dexie, { type Table } from "dexie";
import { nanoid } from "nanoid";
import type { AssetMeta, Doc, DocID, DocSummary } from "./types";
import type { StorageAdapter } from "./adapter";

type DocRow = Doc;
type AssetRow = AssetMeta & { blob: Blob };

class PloomDB extends Dexie {
  docs!: Table<DocRow, string>;
  assets!: Table<AssetRow, string>;
  constructor() {
    super("ploom-db");
    this.version(1).stores({
      docs: "id, updatedAt, createdAt",
      assets: "blobKey",
    });
  }
}

const db = new PloomDB();
const SCHEMA_VERSION = 1;

export class LocalIndexedDBStorage implements StorageAdapter {
  async list(): Promise<DocSummary[]> {
    const docs = await db.docs.orderBy("updatedAt").reverse().toArray();
    return docs.map(({ id, title, createdAt, updatedAt }) => ({ id, title, createdAt, updatedAt }));
  }

  async create(init?: Partial<Doc>): Promise<Doc> {
    const id = init?.id ?? nanoid();
    const now = new Date().toISOString();
    const title = init?.title ?? "Untitled";
    const json = init?.json ?? { type: "doc", content: [{ type: "paragraph" }] };
    const row: DocRow = {
      id,
      title,
      createdAt: init?.createdAt ?? now,
      updatedAt: init?.updatedAt ?? now,
      json,
      markdown: init?.markdown ?? "",
      html: init?.html ?? "",
      schemaVersion: SCHEMA_VERSION,
    };
    await db.docs.put(row);
    return row;
  }

  async read(id: DocID): Promise<Doc | null> {
    return (await db.docs.get(id)) ?? null;
  }

  async save(id: DocID, patch: Partial<Doc>): Promise<Doc> {
    const existing = await db.docs.get(id);
    if (!existing) throw new Error("Doc not found");
    const updated: Doc = {
      ...existing,
      ...patch,
      updatedAt: new Date().toISOString(),
    };
    await db.docs.put(updated);
    return updated;
  }

  async remove(id: DocID): Promise<void> {
    await db.docs.delete(id);
  }

  async putAsset(blob: Blob): Promise<{ blobKey: string; meta: AssetMeta }> {
    const blobKey = nanoid();
    const meta: AssetMeta = {
      blobKey,
      mime: blob.type || "application/octet-stream",
      size: blob.size,
      refCount: 1,
      createdAt: new Date().toISOString(),
    };
    await db.assets.put({ ...meta, blob });
    return { blobKey, meta };
  }

  async getAsset(blobKey: string): Promise<Blob | null> {
    const row = await db.assets.get(blobKey);
    return row?.blob ?? null;
  }

  async gcAssets(): Promise<void> {
    // Placeholder: in future, scan docs for references and drop unreferenced blobs
    return;
  }
}

export function getLocalAdapter(): StorageAdapter {
  return new LocalIndexedDBStorage();
}

export function getCurrentDocId(): string | null {
  try {
    return window.localStorage.getItem("ploom_current_doc");
  } catch {
    return null;
  }
}

export function setCurrentDocId(id: string) {
  try {
    window.localStorage.setItem("ploom_current_doc", id);
  } catch {}
}

